import { ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';
import { cx } from './cx';
import { Icon } from './Icon';
import { Text } from './Text';
import { Badge } from './Badge';
import { Button } from './Button';
import { EntityRef } from './EntityRef';
import { useT } from '../../modules/i18n';

export interface WorkflowCardProps {
  title: string;
  description: ReactNode;
  /** A lucide icon shown in the brand-tinted badge. */
  icon?: ReactNode;
  tags?: string[];
  owner?: string;
  /** Launch action; renders the primary button when provided. */
  onRun?: () => void;
  runLabel?: string;
  className?: string;
}

/**
 * Workflow card (backed by a scaffolder template) — icon badge, title and
 * description, tag badges + owner, and a launch action. Used on the Actions
 * page in place of the stock scaffolder template card.
 */
export const WorkflowCard = ({
  title,
  description,
  icon,
  tags,
  owner,
  onRun,
  runLabel,
  className,
}: WorkflowCardProps) => {
  const t = useT();
  const resolvedRunLabel = runLabel ?? t('ui.workflowCard.runLabel');
  return (
    <div
      className={cx(
        'flex h-full flex-col gap-3 rounded-xl border border-default bg-surface p-5 shadow-xs',
        className,
      )}
    >
      <div className="flex items-center gap-3">
        {icon && (
          <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-lg bg-brand-solid/12 text-brand-text">
            {icon}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <Text variant="body" className="font-semibold">
            {title}
          </Text>
          {owner && (
            <EntityRef
              entityRef={owner}
              className="mt-0.5 text-xs text-fg-tertiary"
            />
          )}
        </div>
      </div>

      <Text variant="body" tone="tertiary" className="line-clamp-3">
        {description}
      </Text>

      {tags && tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {tags.map(tag => (
            <Badge key={tag} color="gray">
              {tag}
            </Badge>
          ))}
        </div>
      )}

      {onRun && (
        <div className="mt-auto flex justify-end border-t border-default pt-3">
          <Button
            variant="primary"
            size="sm"
            onPress={onRun}
            iconTrailing={<Icon as={ArrowRight} size="sm" />}
          >
            {resolvedRunLabel}
          </Button>
        </div>
      )}
    </div>
  );
};
